/**
 * Environment variable configuration with type safety and Zod validation
 */

import { z } from "zod";
import { logger } from "@/utils/logger";

// Zod schema for environment variables
const envSchema = z.object({
  // App configuration
  VITE_APP_NAME: z.string().default("Becoming Alpha"),
  VITE_APP_URL: z.url().default("https://balpha.co"),
  VITE_API_URL: z.url().default("https://api.dev.balpha.co"),
  VITE_APP_VERSION: z.string().optional(),

  // Analytics
  VITE_GA_MEASUREMENT_ID: z.string().optional(),

  // Error tracking
  VITE_SENTRY_DSN: z.union([z.url(), z.literal("")]).optional(),
  VITE_ENABLE_SENTRY_DEV: z
    .string()
    .optional()
    .transform((val) => val === "true" || val === "1"),

  // Marketing pixels
  VITE_META_PIXEL_ID: z.string().optional(),
  VITE_TWITTER_PIXEL_ID: z.string().optional(),
  VITE_TIKTOK_PIXEL_ID: z.string().optional(),

  // Feature flags
  VITE_ENABLE_CHAT: z
    .string()
    .default("false")
    .transform((val) => (val ?? "false") === "true"),

  // Vite built-in
  DEV: z.boolean().optional(),
  PROD: z.boolean().optional(),
});

// Parse and validate environment variables
const parseEnv = () => {
  const rawEnv = {
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
    VITE_APP_URL: import.meta.env.VITE_APP_URL,
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
    VITE_GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID,
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_ENABLE_SENTRY_DEV: import.meta.env.VITE_ENABLE_SENTRY_DEV,
    VITE_META_PIXEL_ID: import.meta.env.VITE_META_PIXEL_ID,
    VITE_TWITTER_PIXEL_ID: import.meta.env.VITE_TWITTER_PIXEL_ID,
    VITE_TIKTOK_PIXEL_ID: import.meta.env.VITE_TIKTOK_PIXEL_ID,
    VITE_ENABLE_CHAT: import.meta.env.VITE_ENABLE_CHAT,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
  };

  try {
    return envSchema.parse(rawEnv);
  } catch (error) {
    if (error instanceof z.ZodError) {
      logger.error("[Env Config] Environment variable validation failed:", error.issues);
      // In development, throw to make issues visible
      throw new Error(
        `Environment variable validation failed:\n${error.issues
          .map((e) => `  - ${e.path.join(".")}: ${e.message}`)
          .join("\n")}`
      );
    }
    throw error;
  }
};

const parsedEnv = parseEnv();

// Exported environment configuration
export interface EnvConfig {
  appName: string;
  appUrl: string;
  apiUrl: string;
  appVersion?: string;
  isDevelopment: boolean;
  isProduction: boolean;
  // Analytics
  gaMeasurementId: string;
  // Error tracking
  sentryDsn: string;
  enableSentryDev: boolean;
  // Marketing pixels
  metaPixelId: string;
  twitterPixelId: string;
  tiktokPixelId: string;
  // Feature flags
  enableChat: boolean;
}

export const env: EnvConfig = {
  appName: parsedEnv.VITE_APP_NAME,
  appUrl: parsedEnv.VITE_APP_URL,
  apiUrl: parsedEnv.VITE_API_URL,
  appVersion: parsedEnv.VITE_APP_VERSION,
  isDevelopment: parsedEnv.DEV ?? import.meta.env.DEV ?? false,
  isProduction: parsedEnv.PROD ?? import.meta.env.PROD ?? false,
  gaMeasurementId: parsedEnv.VITE_GA_MEASUREMENT_ID || "G-0HNJBEX94D",
  sentryDsn: parsedEnv.VITE_SENTRY_DSN || "",
  enableSentryDev: parsedEnv.VITE_ENABLE_SENTRY_DEV ?? false,
  metaPixelId: parsedEnv.VITE_META_PIXEL_ID || "",
  twitterPixelId: parsedEnv.VITE_TWITTER_PIXEL_ID || "",
  tiktokPixelId: parsedEnv.VITE_TIKTOK_PIXEL_ID || "",
  enableChat: parsedEnv.VITE_ENABLE_CHAT,
};

// Validate required environment variables
const validateEnv = () => {
  const required: (keyof EnvConfig)[] = ["appName", "appUrl", "apiUrl", "enableChat"];

  for (const key of required) {
    if (!env[key]) {
      logger.warn(`Warning: Environment variable for ${key} is not set`);
    }
  }
};

if (env.isDevelopment) {
  validateEnv();
}
